import React, { createContext, useState, useContext, useCallback, useMemo } from 'react'
import { FormData, StepContextType, StepProviderProps } from './types'
import { ValidationErrors } from './validationTypes'
import { validateField as validateSingleField, isStepValid, isFormValid as checkFormValidity } from './validationUtils'
import { getChannelFromPath } from '@/components/utils/channelMappingUtils'
import { markIntent } from '@/components/utils/registrationIntent'

export const StepContext = createContext<StepContextType | null>(null)

const defaultFormData: FormData = {
    workingStatus: '',
    experienceYears: '',
    prefecture: '',
    lastName: '',
    firstName: '',
    age: '',
    email: '',
    phone: '',
    lineId: '',
    preferLineContact: false,
    agreeTerms: false,
    channel: '',
    jobChangeInterest: ''
}

const defaultTouchedFields: Record<keyof FormData, boolean> = {
    workingStatus: false,
    experienceYears: false,
    prefecture: false,
    lastName: false,
    firstName: false,
    age: false,
    email: false,
    phone: false,
    lineId: false,
    preferLineContact: false,
    agreeTerms: false,
    channel: false,
    jobChangeInterest: false
}

export const StepProvider: React.FC<StepProviderProps> = ({ 
    children, 
    initialFormData = {}, 
    initialTouchedFields = {} 
}) => {
    const [currentStep, setCurrentStep] = useState(0)
    const [isAnimating, setIsAnimating] = useState(false)

    // channelが指定されていない場合はgetChannelFromPathを使用
    const channel = initialFormData.channel ?? getChannelFromPath()

    const [formData, setFormData] = useState<FormData>({
        ...defaultFormData,
        ...initialFormData,
        channel
    })

    const [errors, setErrors] = useState<ValidationErrors>({})
    const [touchedFields, setTouchedFields] = useState<Record<keyof FormData, boolean>>({
        ...defaultTouchedFields,
        ...initialTouchedFields
    })

    const updateFormData = useCallback((field: keyof FormData, value: string | boolean) => {
        setFormData(prevData => {
            const newData = { ...prevData, [field]: value }

            const fieldError = validateSingleField(field, value, newData)

            setErrors(prev => ({
                ...prev,
                [field]: fieldError || undefined
            }))

            return newData
        })

        if (!touchedFields[field]) {
            setTouchedFields(prev => ({
                ...prev,
                [field]: true
            }))
        }
    }, [touchedFields])
 
    const touchField = useCallback((field: keyof FormData) => {
        if (!touchedFields[field]) {
            setTouchedFields(prev => ({
                ...prev,
                [field]: true
            }))

            const fieldError = validateSingleField(field, formData[field] as string | boolean, formData)
            setErrors(prev => ({
                ...prev,
                [field]: fieldError || undefined
            }))
        }
    }, [formData, touchedFields])

    const validateField = useCallback((field: keyof FormData): string | null => {
        const error = validateSingleField(field, formData[field] as string | boolean, formData)

        setErrors(prev => ({
            ...prev,
            [field]: error || undefined
        }))

        return error
    }, [formData])

    const isCurrentStepValid = useMemo(() => {
        return isStepValid(currentStep, formData)
    }, [currentStep, formData])

    const isFormValid = useMemo(() => {
        return checkFormValidity(formData)
    }, [formData])

    const handleNext = useCallback(() => {
        if (currentStep < 4 && !isAnimating) {
            const nextStep = currentStep + 1
            setIsAnimating(true)
            setCurrentStep(nextStep)

            // ステップを1つでも進めた人は「登録意思がある人」として記録する。
            // GENIEEのLINE登録促進モーダルの抑止に使う（utils/genieeLinePromo）。
            // 縦型EF・横型EF・EntryFormSlideAppはいずれもこのStepProviderを通るため、
            // 記録はここ1箇所で足りる（どのページで抑止に使うかはgenieeLinePromo側で判定する）
            if (nextStep >= 1) markIntent('step_advanced')

            setTimeout(() => {
                setIsAnimating(false)
            }, 500)
        }
    }, [currentStep, isAnimating])

    const handlePrev = useCallback(() => {
        if (currentStep > 0 && !isAnimating) {
            setIsAnimating(true)
            setCurrentStep(currentStep - 1)

            setTimeout(() => {
                setIsAnimating(false)
            }, 500)
        }
    }, [currentStep, isAnimating])

    const contextValue: StepContextType = {
        currentStep,
        isAnimating,
        formData,
        updateFormData,
        handleNext,
        handlePrev,
        errors,
        validateField,
        isCurrentStepValid,
        isFormValid,
        touchField,
        touchedFields
    }

    return (
        <StepContext.Provider value={contextValue}>
            {typeof children === 'function' ? children(contextValue) : children}
        </StepContext.Provider>
    )
}

export const useStepContext = (): StepContextType => {
    const context = useContext(StepContext)
    if (!context) {
        throw new Error('useStepContext must be used within a StepProvider')
    }
    return context
}
